import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { SearchKind } from '../../api/search';

import type { PaletteCommand } from './commandPalette.types';
import { NAV_THEMES, type NavTheme } from './nav-tokens';
import PaletteChips from './PaletteChips';
import PaletteInput from './PaletteInput';
import PaletteResults from './PaletteResults';
import { isSearchableTerm } from './search/search-kinds';
import { usePaletteHighlight } from './search/use-palette-highlight';
import { usePaletteSections } from './search/use-palette-sections';
import { useModalLayer } from './useModalLayer';

interface CommandPaletteProps {
  theme: NavTheme;
  onClose: () => void;
}

/** ⌘K palette: commands plus search across venues, bookings and private events. */
export default function CommandPalette({ theme, onClose }: CommandPaletteProps) {
  const t = NAV_THEMES[theme];
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<SearchKind | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const navigate = useNavigate();
  const { isTopmost } = useModalLayer('search-palette');

  const showChips = isSearchableTerm(query);
  // A chip only filters while the chips are on screen to show it.
  const activeKind = showChips ? kind : null;
  const { sections, status, kinds, remember } = usePaletteSections(query, activeKind);
  const rows = sections.flatMap((s) => s.rows);

  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      // Gated on the modal stack: the palette can open over the nav drawer,
      // which listens for Escape on `window` too, and one press must dismiss
      // only the layer on top.
      if (e.key === 'Escape' && isTopmost()) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, isTopmost]);

  function run(cmd: PaletteCommand) {
    onClose();
    if (cmd.recent) remember(cmd.recent);
    if (cmd.onSelect) cmd.onSelect();
    else if (cmd.to) navigate(cmd.to);
  }

  const highlight = usePaletteHighlight(rows, run);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-50 flex items-start justify-center"
      style={{ background: 'rgba(15, 25, 18, 0.42)', paddingTop: '12vh' }}
      onClick={onClose}
    >
      <div
        className="flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          maxWidth: '90vw',
          background: t.bg,
          border: `1px solid ${t.border}`,
          borderRadius: 12,
          boxShadow: '0 24px 60px rgba(0,0,0,.35)',
        }}
      >
        <PaletteInput
          value={query}
          onChange={setQuery}
          onKeyDown={highlight.onKeyDown}
          inputRef={inputRef}
          listId={listId}
          activeOptionId={highlight.activeId ? `${listId}-${highlight.activeId}` : null}
          theme={theme}
        />
        {showChips && kinds.length > 1 && (
          <PaletteChips kinds={kinds} selected={activeKind} onSelect={setKind} theme={theme} />
        )}
        <PaletteResults
          listId={listId}
          sections={sections}
          status={status}
          activeId={highlight.activeId}
          theme={theme}
          onRun={run}
          onHover={highlight.setActiveId}
        />
      </div>
    </div>
  );
}
